import { useMemo, useState } from 'react';
import type { TablePaginationConfig } from 'antd';

interface UseListParamsOptions {
  /** Initial / fixed page size. Defaults to 10. */
  pageSize?: number;
}

interface UseListParamsResult {
  search: string;
  page: number;
  pageSize: number;
  /** Setter that also resets to page 1 (matches the expected list-search UX). */
  setSearch: (next: string) => void;
  setPage: (next: number) => void;
  /**
   * Standard AntD `Table.onChange` handler that extracts `current` from the
   * pagination argument and updates the page state. Handles the union type
   * `false | TablePaginationConfig | undefined` safely.
   */
  handleTableChange: (pagination: false | TablePaginationConfig | undefined) => void;
  /** Params shaped for typical paginated API requests (page/limit/search). */
  params: { page: number; limit: number; search: string | undefined };
}

/**
 * Local UI state for a server-paginated list view (search + page).
 *
 * Replaces the ad-hoc `useState('')` + `useState(1)` + brittle inline
 * pagination handler that every Upaya list view was duplicating.
 *
 * NOTE: this hook owns ONLY the search/page state. Domain-specific filters
 * (assignment status, date range, status tag, etc.) stay in the caller — that
 * way each view's filter shape remains explicit and typed at its callsite.
 */
export function useListParams({ pageSize = 10 }: UseListParamsOptions = {}): UseListParamsResult {
  const [search, setSearchState] = useState('');
  const [page, setPage] = useState(1);

  const setSearch = (next: string) => {
    setSearchState(next);
    setPage(1);
  };

  const handleTableChange = (pagination: false | TablePaginationConfig | undefined) => {
    if (!pagination || typeof pagination !== 'object') return;
    const next = pagination.current;
    if (typeof next === 'number') setPage(next);
  };

  const params = useMemo(
    () => ({ page, limit: pageSize, search: search.trim() ? search.trim() : undefined }),
    [page, pageSize, search],
  );

  return {
    search,
    page,
    pageSize,
    setSearch,
    setPage,
    handleTableChange,
    params,
  };
}
